import { useCallback, useRef, useState } from 'react';
import { toOffersRequest } from '../schemas';
import { offersApi } from '../services/offersApi';
import { useAppDispatch } from '../store/store';
import { fanOutSearch } from './fanOut';
// Drives a city/region fan-out. The pure orchestration lives in `fanOutSearch`;
// this hook only injects the RTK Query-backed per-property call and tracks the
// loading/result state. A run counter guards against a slow earlier fan-out
// overwriting the results of a newer search.
export const useMarketSearch = () => {
    const dispatch = useAppDispatch();
    const [state, setState] = useState({ status: 'idle' });
    const runId = useRef(0);
    const run = useCallback(async (properties, request) => {
        const thisRun = ++runId.current;
        setState({ status: 'loading' });
        const searchOne = async (property) => {
            const subscription = dispatch(offersApi.endpoints.searchOffers.initiate(toOffersRequest(property.id, request)));
            try {
                const data = await subscription.unwrap();
                return data.offers;
            }
            catch (error) {
                // Fan-out tolerates per-property failure (the UI names the property),
                // but the reason must not vanish — log it at the IO boundary before
                // it collapses into a failed outcome.
                console.warn(`Sales Desk: offers search failed for property ${property.id}`, error);
                throw error;
            }
            finally {
                subscription.unsubscribe();
            }
        };
        const result = await fanOutSearch(properties, searchOne);
        if (thisRun === runId.current) {
            setState({ status: 'done', result });
        }
    }, [dispatch]);
    return { state, run };
};
